import { gql, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';

import { updateUser } from '../../../actions/entities';
import useGetCompanyVerificationInfo from '../../../common/hooks/useGetCompanyVerificationInfo';
import { User } from '../../../models/User';
import { getUser } from '../../../selectors/user';
import { useAppDispatch } from '../../../store';

const mutation = gql`
  mutation updateJobSlotUpgradedModalSeenSlots($input: UpdateMeInput!) {
    updateJobSlotUpgradedModalSeenSlots: updateMe(me: $input) {
      id
      updatedAt
      metadata
    }
  }
`;

type QueryInput = {
  input: Record<string, unknown>;
};

type QueryOutput = {
  updateJobSlotUpgradedModalSeenSlots: Pick<
    User,
    'updatedAt' | 'id' | 'metadata'
  >;
};

export function useJobSlotUpgradedModal() {
  const [updateJobSlotUpgradedModalSeenSlotsMutation] = useMutation<
    QueryOutput,
    QueryInput
  >(mutation);
  const dispatch = useAppDispatch();
  const user: User = useSelector(getUser);

  const { isAutoUpgraded, company } = useGetCompanyVerificationInfo();

  const totalConcurrentJobSlots =
    company?.currentCompanyJobSlotsSubscription?.totalConcurrentJobSlots;

  if (!isAutoUpgraded) return null;

  if (!totalConcurrentJobSlots) return null;

  const jobSlotUpgradedModalSeenSlots = (
    user?.metadata as Record<string, unknown>
  )?.jobSlotUpgradedModalSeenSlots;

  const shouldShow =
    jobSlotUpgradedModalSeenSlots !== String(totalConcurrentJobSlots);

  if (!shouldShow) return null;

  const markSeen = () => {
    const metadataToUpdate = {
      jobSlotUpgradedModalSeenSlots: String(totalConcurrentJobSlots),
    };
    dispatch(
      updateUser(user.id, {
        metadata: {
          ...(user.metadata as Record<string, unknown>),
          ...metadataToUpdate,
        },
      })
    );
    updateJobSlotUpgradedModalSeenSlotsMutation({
      variables: { input: { metadata: metadataToUpdate } },
    });
  };

  return {
    newTotalJobSlots: totalConcurrentJobSlots,
    markSeen,
  } as const;
}
